// ABOUTME: Data sources list page showing all connected data sources.
// ABOUTME: Allows users to view existing sources and add additional ones.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Database, FileSpreadsheet, ShoppingBag, Loader2, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { apiFetch } from '@/services/api/client';
import { useApp } from '@/context/AppContext';
import type { DataSource } from '@/types/api';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

export function DataSourcesPage() {
  const navigate = useNavigate();
  const { currentWorkspaceId } = useApp();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDataSources = useCallback(async () => {
    try {
      const profile = await apiFetch<{
        primary_workspace_id: string;
        workspaces: Array<{ workspace_id: string; data_sources?: DataSource[] }>;
      }>('/users/me');
      const ws = profile.workspaces?.find(
        (w) => w.workspace_id === currentWorkspaceId
      ) ?? profile.workspaces?.find(
        (w) => w.workspace_id === profile.primary_workspace_id
      ) ?? profile.workspaces?.[0];
      const allSources: DataSource[] = ws?.data_sources ?? [];
      setDataSources(allSources);
    } catch (err) {
      console.error('Failed to fetch data sources:', err);
      setDataSources([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  const getTypeIcon = (ds: DataSource) => {
    const displayType = ds.display_type || ds.type;
    if (displayType === 'shopify') return <ShoppingBag className="w-5 h-5 text-primary-600" />;
    if (displayType === 'csv') return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    return <Database className="w-5 h-5 text-blue-600" />;
  };

  const getBadgeClass = (ds: DataSource) => {
    const displayType = ds.display_type || ds.type;
    if (displayType === 'shopify') return 'bg-primary-100 text-primary-700';
    if (displayType === 'csv') return 'bg-green-100 text-green-700';
    if (displayType === 'databricks') return 'bg-blue-100 text-blue-700';
    if (displayType === 'delta_sharing') return 'bg-blue-100 text-blue-700';
    if (displayType === 'legend') return 'bg-purple-100 text-purple-700';
    return 'bg-surface-100 text-surface-700';
  };

  const getTypeBadge = (ds: DataSource) => {
    const displayType = ds.display_type || ds.type;
    if (displayType === 'shopify') return { label: 'Shopify', className: getBadgeClass(ds) };
    if (displayType === 'csv') return { label: 'CSV', className: getBadgeClass(ds) };
    if (displayType === 'databricks') return { label: 'Databricks', className: getBadgeClass(ds) };
    if (displayType === 'delta_sharing') return { label: 'Delta Share', className: getBadgeClass(ds) };
    if (displayType === 'legend') return { label: 'Legend', className: getBadgeClass(ds) };
    return { label: displayType, className: getBadgeClass(ds) };
  };

  const getStatusBadge = (status?: string) => {
    if (status === 'connected') return { label: 'Connected', className: 'bg-green-100 text-green-700' };
    if (status === 'error') return { label: 'Error', className: 'bg-red-100 text-red-700' };
    if (status === 'disabled') return { label: 'Disabled', className: 'bg-surface-100 text-surface-500' };
    return { label: status || 'Unknown', className: 'bg-surface-100 text-surface-500' };
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDataSourceClick = (ds: DataSource) => {
    if (ds.type === 'csv') {
      navigate(`/data-sources/${ds.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-surface-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading data sources...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="space-y-8"
        >
          {/* Header */}
          <motion.div variants={item}>
            <h1 className="text-2xl font-semibold text-surface-900">
              Data Sources
            </h1>
            <p className="text-surface-500 mt-1">
              Data sources available in your workspace
            </p>
          </motion.div>

          {/* Data source list */}
          {dataSources.length === 0 ? (
            <motion.div variants={item}>
              <Card variant="glass" className="p-12 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-surface-100 flex items-center justify-center">
                  <Database className="w-7 h-7 text-surface-400" />
                </div>
                <h2 className="text-lg font-semibold text-surface-900 mb-2">
                  No data sources configured
                </h2>
                <p className="text-surface-500 max-w-sm mx-auto">
                  Contact your administrator to configure a data source for this workspace.
                </p>
              </Card>
            </motion.div>
          ) : (
            <motion.div variants={item} className="space-y-3">
              {dataSources.map((ds) => {
                const typeBadge = getTypeBadge(ds);
                const statusBadge = getStatusBadge(ds.status);
                const isClickable = ds.type === 'csv';

                return (
                  <button
                    key={ds.id}
                    onClick={() => handleDataSourceClick(ds)}
                    disabled={!isClickable}
                    className={`w-full flex items-center gap-4 p-4 rounded-sm border border-surface-200 bg-surface-0 text-left transition-colors ${
                      isClickable
                        ? 'hover:border-surface-300 hover:bg-surface-50 cursor-pointer'
                        : 'cursor-default'
                    }`}
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-surface-100 flex items-center justify-center">
                      {getTypeIcon(ds)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-surface-900 truncate">
                          {ds.name}
                        </span>
                        {ds.is_demo && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                            Demo
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-surface-500 mt-0.5">
                        {ds.type === 'csv' && ds.csv_files
                          ? `${ds.csv_files.length} file${ds.csv_files.length !== 1 ? 's' : ''}`
                          : 'Connected'}
                        {' '}
                        <span className="text-surface-400">
                          &middot; Added {formatDate(ds.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeBadge.className}`}>
                        {typeBadge.label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                    </div>

                    {/* Arrow for clickable items */}
                    {isClickable && (
                      <ExternalLink className="w-4 h-4 text-surface-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
